/**
 * Frontend service for Zoho Books Sales Orders.
 *
 * All calls go through the `zohoServiceOrders` Firebase Callable Function —
 * Zoho credentials are never exposed to the browser.
 *
 * A Sales Order is correlated to a Wix booking via its `reference_number`
 * field (= the Wix booking ID). No Firestore storage is used.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { WixBookingData, GroupedInvoiceResult } from '../../functions/src/services/zohoServiceOrderService';
import type {
  ZohoSalesOrderLineItem,
  ZohoSalesOrder,
  ZohoPricelist,
  ZohoItem,
  ZohoTax,
} from '@rsmaids/shared/types/zoho';

export type { ZohoSalesOrderLineItem, ZohoSalesOrder, ZohoPricelist, ZohoItem, ZohoTax, WixBookingData, GroupedInvoiceResult };

const functions = getFunctions();
const _zohoServiceOrdersFn = httpsCallable<
  { action: string; bookingId?: string; booking?: WixBookingData; search?: string; module?: string; newSoNumber?: string; salesorderId?: string; page?: number; perPage?: number; date_start?: string; date_end?: string; feeAmount?: number; feePercent?: number; customerId?: string; month?: string; dryRun?: boolean; salesorderIds?: string[]; contactId?: string },
  { salesOrder: ZohoSalesOrder | null; orders?: ZohoSalesOrder[]; hasMore?: boolean; pricelists?: ZohoPricelist[]; items?: ZohoItem[]; customFieldOptions?: string[]; serviceMonthCustomFieldId?: string; taxes?: ZohoTax[]; defaults?: { nextSoNumber: string; notes: string; terms: string }; invoiceId?: string; invoiceNumber?: string; grouped?: GroupedInvoiceResult; completion?: CompleteInvoiceResult }
>(functions, 'zohoServiceOrders', { timeout: 60000 });

/** What completing a booking did on the server. No amounts: any team member may call it. */
export type CompleteInvoiceResult = 'created' | 'alreadyInvoiced' | 'notPerOrder' | 'noServiceOrder';

// ─── API ──────────────────────────────────────────────────────────────────────

/**
 * Fetch the Zoho Sales Order linked to a Wix booking.
 * Returns null if no order has been created yet.
 */
export async function getServiceOrder(bookingId: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({ action: 'get', bookingId });
  return result.data.salesOrder;
}

/**
 * Create a Zoho Sales Order for the given booking.
 * Idempotent — if one already exists it returns the existing one.
 */
export async function createServiceOrder(booking: WixBookingData): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({
    action: 'create',
    bookingId: booking.bookingId,
    booking,
  });
  return result.data.salesOrder;
}

/**
 * Update the Zoho Sales Order when a booking is rescheduled, or when editing
 * an order directly. `salesorderId` (when already known, e.g. editing an
 * existing order) identifies it directly instead of the usual
 * reference_number lookup — required for a standalone order (no real Wix
 * booking), whose reference_number is empty by design, so the lookup would
 * otherwise silently find nothing and save nothing.
 */
export async function updateServiceOrder(booking: WixBookingData, salesorderId?: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({
    action: 'update',
    bookingId: booking.bookingId,
    booking,
    salesorderId,
  });
  return result.data.salesOrder;
}

/**
 * Void (cancel) the Zoho Sales Order linked to a booking. `salesorderId`
 * (when already known) identifies it directly instead of the usual
 * reference_number lookup — required for a standalone order (no real Wix
 * booking), whose reference_number is empty by design.
 */
export async function cancelServiceOrder(bookingId: string, salesorderId?: string): Promise<void> {
  await _zohoServiceOrdersFn({ action: 'cancel', bookingId, salesorderId });
}

/**
 * Apply a cancellation fee instead of voiding the SO — zeroes out the
 * existing line items and adds a single "Cancellation Fee" line item for
 * the given amount. `salesorderId` — see cancelServiceOrder's doc comment.
 */
export async function applyCancellationFee(bookingId: string, fee: number | { percent: number }, salesorderId?: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({
    action: 'applyCancellationFee',
    bookingId,
    ...(typeof fee === 'number' ? { feeAmount: fee } : { feePercent: fee.percent }),
    salesorderId,
  });
  return result.data.salesOrder;
}

/**
 * Confirm a draft Zoho Sales Order (draft → open), without sending an email.
 * `salesorderId` — see cancelServiceOrder's doc comment.
 */
export async function confirmServiceOrder(bookingId: string, salesorderId?: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({ action: 'confirmOrder', bookingId, salesorderId });
  return result.data.salesOrder;
}

/**
 * Permanently delete a Zoho Sales Order by its Zoho ID.
 * Irreversible — unlike cancelServiceOrder() (void), this frees the SO number for reuse.
 */
export async function deleteServiceOrder(salesorderId: string): Promise<void> {
  await _zohoServiceOrdersFn({ action: 'deleteOrder', bookingId: '', salesorderId });
}

/**
 * Clone an existing Sales Order into a new draft SO (same customer, line
 * items, and terms — not linked to any booking). Returns the new order.
 */
export async function cloneServiceOrder(salesorderId: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({ action: 'cloneOrder', bookingId: '', salesorderId });
  return result.data.salesOrder;
}

/**
 * The invoicing half of Complete, decided on the server so the caller never receives the billing record or the order: a "per
 * order" customer gets the instant invoice from the booking's service order unless it is already invoiced; grouped / manual
 * customers are not invoiced here. Callers record the attendance themselves afterwards. `contactId` is only used when the
 * booking record has none.
 */
export async function completeBookingInvoice(bookingId: string, contactId?: string): Promise<{ completion: CompleteInvoiceResult; invoiceNumber?: string }> {
  const result = await _zohoServiceOrdersFn({ action: 'completeBookingInvoice', bookingId, ...(contactId ? { contactId } : {}) });
  return { completion: result.data.completion ?? 'notPerOrder', invoiceNumber: result.data.invoiceNumber };
}

/**
 * Create a Zoho Books invoice from an existing Sales Order (carries over
 * its line items). Returns the new invoice's ID/number.
 */
export async function createInvoiceFromServiceOrder(salesorderId: string): Promise<{ invoiceId?: string; invoiceNumber?: string }> {
  const result = await _zohoServiceOrdersFn({ action: 'createInvoiceFromOrder', bookingId: '', salesorderId });
  return { invoiceId: result.data.invoiceId, invoiceNumber: result.data.invoiceNumber };
}

/**
 * One invoice for all of a customer's confirmed sales orders in a month ("YYYY-MM").
 * `dryRun` only previews which orders would be included; pass false to create it.
 * `salesorderIds` limits the invoice to those orders (omit for all confirmed ones).
 */
export async function createGroupedInvoice(customerId: string, month: string, dryRun: boolean, salesorderIds?: string[]): Promise<GroupedInvoiceResult> {
  const result = await _zohoServiceOrdersFn({ action: 'createGroupedInvoice', bookingId: '', customerId, month, dryRun, salesorderIds });
  if (!result.data.grouped) throw new Error('No grouped invoice result returned');
  return result.data.grouped;
}

/**
 * Fetch everything needed for the ServiceOrderModal in a single call.
 */
export async function fetchZohoAll(): Promise<{
  items: ZohoItem[];
  taxes: ZohoTax[];
  pricelists: ZohoPricelist[];
  serviceMonthOptions: string[];
  serviceMonthCustomFieldId: string;
  defaults: { nextSoNumber: string; notes: string; terms: string };
}> {
  const result = await _zohoServiceOrdersFn({ action: 'listAll', bookingId: '' });
  return {
    items:                     result.data.items              ?? [],
    taxes:                     result.data.taxes              ?? [],
    pricelists:                result.data.pricelists         ?? [],
    serviceMonthOptions:       result.data.customFieldOptions ?? [],
    serviceMonthCustomFieldId: result.data.serviceMonthCustomFieldId ?? '',
    defaults:                  result.data.defaults           ?? { nextSoNumber: '', notes: '', terms: '' },
  };
}

/**
 * Fetch just items + pricelists — the lean data set used by the Price Calculator.
 * Unlike fetchZohoAll(), skips taxes, custom fields, recent sales orders, and the
 * SO-detail fetch, none of which the calculator uses. Much faster.
 */
export async function fetchZohoPricingData(): Promise<{ items: ZohoItem[]; pricelists: ZohoPricelist[] }> {
  const result = await _zohoServiceOrdersFn({ action: 'listPricingData', bookingId: '' });
  return {
    items:      result.data.items      ?? [],
    pricelists: result.data.pricelists ?? [],
  };
}

/**
 * Fetch all active Zoho Books pricelists (pricebooks).
 */
export async function fetchZohoPricelists(): Promise<ZohoPricelist[]> {
  const result = await _zohoServiceOrdersFn({ action: 'listPricelists', bookingId: '' });
  return result.data.pricelists ?? [];
}

/**
 * Fetch Zoho Books items/products (active, up to 200).
 */
export async function fetchZohoItems(): Promise<ZohoItem[]> {
  const result = await _zohoServiceOrdersFn({ action: 'listItems', bookingId: '' });
  return result.data.items ?? [];
}

/**
 * Fetch the allowed values (and customfield_id) for the "Service Month" custom
 * field on the given Zoho Books module. Sales Orders and Invoices each define
 * their own separate "Service Month" custom field in Zoho — pass the module
 * whose field you need. Returns empty options if the field has no picklist.
 */
export async function fetchZohoServiceMonths(
  module: 'salesorders' | 'invoice' = 'salesorders',
): Promise<{ options: string[]; customFieldId: string }> {
  const result = await _zohoServiceOrdersFn({ action: 'listCustomFields', bookingId: '', module });
  return {
    options: result.data.customFieldOptions ?? [],
    customFieldId: result.data.serviceMonthCustomFieldId ?? '',
  };
}

/**
 * Fetch all active Zoho Books taxes.
 */
export async function fetchZohoTaxes(): Promise<ZohoTax[]> {
  const result = await _zohoServiceOrdersFn({ action: 'listTaxes', bookingId: '' });
  return result.data.taxes ?? [];
}

/**
 * Fetch Zoho Books SO defaults: next SO number, default notes, default terms.
 */
export async function fetchZohoDefaults(): Promise<{ nextSoNumber: string; notes: string; terms: string }> {
  const result = await _zohoServiceOrdersFn({ action: 'listDefaults', bookingId: '' });
  return result.data.defaults ?? { nextSoNumber: '', notes: '', terms: '' };
}

/**
 * Fetch items with pricelist-specific rates for a given pricebook ID.
 */
export async function fetchZohoPricelistItems(pricelistId: string): Promise<ZohoItem[]> {
  const fn = getFunctions();
  const callable = httpsCallable<
    { action: string; bookingId: string; pricelistId: string },
    { salesOrder: null; items?: ZohoItem[] }
  >(fn, 'zohoServiceOrders', { timeout: 30000 });
  const result = await callable({ action: 'listPricelistItems', bookingId: '', pricelistId });
  return result.data.items ?? [];
}

/**
 * Reset Zoho's SO auto-number sequence so the next auto-assigned number matches
 * the requested value (e.g. after SOs are deleted in Zoho UI and the counter drifts).
 * Returns the new nextSoNumber on success.
 */
export async function resetSoNumberSequence(newSoNumber: string): Promise<string> {
  const result = await _zohoServiceOrdersFn({
    action: 'resetSoNumber',
    bookingId: '',
    newSoNumber,
  } as Parameters<typeof _zohoServiceOrdersFn>[0]);
  return result.data.defaults?.nextSoNumber ?? newSoNumber;
}

/**
 * Return the direct URL to view the Sales Order in Zoho Books.
 */
export function zohoSalesOrderUrl(salesOrderId: string): string {
  return `https://books.zoho.com/app#/salesorders/${salesOrderId}`;
}

/**
 * Fetch one page (25) of Sales Orders (lightweight — no line items) for the
 * Service Orders list page, newest first — mirrors invoices' own list
 * pagination. Pass `page` (1-based) to load further pages as the user
 * scrolls; `search` queries Zoho directly instead of just the current page;
 * `dateParams` filters server-side by SO date, same as invoices' own list.
 */
export async function listServiceOrders(
  search?: string, page = 1, dateParams?: { date_start?: string; date_end?: string }, perPage?: number,
): Promise<{ orders: ZohoSalesOrder[]; hasMore: boolean }> {
  const result = await _zohoServiceOrdersFn({
    action: 'listOrders', bookingId: '', search, page, perPage,
    date_start: dateParams?.date_start, date_end: dateParams?.date_end,
  });
  return { orders: result.data.orders ?? [], hasMore: result.data.hasMore ?? false };
}

/**
 * Fetch full detail (line items, notes, terms, linked invoices, etc.) for a
 * single Sales Order by its Zoho ID.
 */
export async function getServiceOrderById(salesorderId: string): Promise<ZohoSalesOrder | null> {
  const result = await _zohoServiceOrdersFn({ action: 'getById', bookingId: '', salesorderId });
  return result.data.salesOrder;
}
